import React, { useMemo } from 'react';

const DNControlReasons = ({ measures }) => {
  // Tableau des correspondances raison/catégorie
  const reasonCategoryMapping = useMemo(() => {
    if (!measures || !measures.length) return [];

    // Créer un map pour stocker la correspondance raison -> catégorie
    const mapping = new Map();

    measures.forEach(measure => {
      if (measure.reason) {
        // Si la raison existe dans le map mais avec une catégorie différente, marquer comme conflit
        if (mapping.has(measure.reason) &&
            mapping.get(measure.reason).category !== measure.reasonCategory) {
          mapping.set(measure.reason, {
            category: measure.reasonCategory || 'Non catégorisé',
            conflict: true
          });
        } else {
          mapping.set(measure.reason, {
            category: measure.reasonCategory || 'Non catégorisé',
            conflict: false
          });
        }
      }
    });

    // Convertir le map en tableau et trier par raison
    return Array.from(mapping.entries())
      .map(([reason, data]) => ({
        reason,
        category: data.category,
        conflict: data.conflict
      }))
      .sort((a, b) => a.reason.localeCompare(b.reason));
  }, [measures]);

  // Identifier les raisons non catégorisées
  const uncategorizedReasons = useMemo(() => {
    return reasonCategoryMapping.filter(item =>
      !item.category || item.category === 'Non catégorisé'
    ).length;
  }, [reasonCategoryMapping]);

  // Identifier les conflits de catégorisation
  const categoryConflicts = useMemo(() => {
    return reasonCategoryMapping.filter(item => item.conflict).length;
  }, [reasonCategoryMapping]);

  if (reasonCategoryMapping.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
        <p className="text-blue-700 dark:text-blue-300">
          Aucune raison d'absence n'a été détectée pour la période sélectionnée.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-0">
      {(uncategorizedReasons > 0 || categoryConflicts > 0) && (
        <div className="flex justify-between items-center mb-3">
          <div className="flex space-x-2">
            {uncategorizedReasons > 0 && (
              <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                {uncategorizedReasons} raison{uncategorizedReasons > 1 ? 's' : ''} non catégorisée{uncategorizedReasons > 1 ? 's' : ''}
              </span>
            )}
            {categoryConflicts > 0 && (
              <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                {categoryConflicts} conflit{categoryConflicts > 1 ? 's' : ''} de catégorie
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Raison
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Catégorie
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {reasonCategoryMapping.map((item, index) => (
                <tr key={index} className={
                  item.conflict
                    ? 'bg-red-50 dark:bg-red-900/10'
                    : (!item.category || item.category === 'Non catégorisé')
                      ? 'bg-yellow-50 dark:bg-yellow-900/10'
                      : ''
                }>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {item.reason}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {item.category || 'Non catégorisé'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {item.conflict ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        Conflit de catégorie
                      </span>
                    ) : !item.category || item.category === 'Non catégorisé' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                        Non catégorisé
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        Ok
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Définir le Badge comme un composant React à part entière
const ReasonsBadge = ({ measures }) => {
  const { uncategorizedCount, conflictCount } = useMemo(() => {
    if (!measures || !measures.length) return { uncategorizedCount: 0, conflictCount: 0 };

    // Logique simplifiée pour calculer les problèmes de mappage
    const reasonMap = new Map();

    measures.forEach(measure => {
      if (measure.reason) {
        const currentCategory = reasonMap.get(measure.reason)?.category;
        if (currentCategory && currentCategory !== measure.reasonCategory) {
          reasonMap.set(measure.reason, {
            category: measure.reasonCategory || 'Non catégorisé',
            conflict: true
          });
        } else {
          reasonMap.set(measure.reason, {
            category: measure.reasonCategory || 'Non catégorisé',
            conflict: false
          });
        }
      }
    });

    const uncategorized = Array.from(reasonMap.values())
      .filter(item => !item.category || item.category === 'Non catégorisé').length;
    const conflicts = Array.from(reasonMap.values())
      .filter(item => item.conflict).length;

    return { uncategorizedCount: uncategorized, conflictCount: conflicts };
  }, [measures]);

  const totalIssues = uncategorizedCount + conflictCount;

  return (
    <>
      {totalIssues > 0 ? (
        <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {totalIssues}
        </span>
      ) : (
        <span className="invisible text-xs px-2 py-0.5 min-w-[20px]">0</span>
      )}
    </>
  );
};

// Assigner le composant à DNControlReasons.Badge
DNControlReasons.Badge = ReasonsBadge;

export default DNControlReasons;